// src/pages/DocumentsCentreDevis.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../axiosInstance";

import {
    TitleRow,
    ToolbarRow,
    TableDocs,
    CostMarginModal,
    ExportModal,
    // helpers & api
    persistEtatDevis,
    totalsWithCost,
    num,
    toMonthKey,
    toMonthLabel,
} from "./centre/SharedCentre";

/* =========================
   Helpers locaux "état devis"
   — clés canoniques & libellés —
   ========================= */
const LABELS_DEVIS = {
    brouillon: "Brouillon",
    envoye: "Envoyé",
    accepte: "Accepté",
    abandonne: "Abandonné",
};
const KEY_BY_LABEL_DEVIS = Object.fromEntries(
    Object.entries(LABELS_DEVIS).map(([k, v]) => [v, k])
);

// Convertit libellé UI -> clé (brouillon/envoye/…)
const labelToStatusDevis = (label) => KEY_BY_LABEL_DEVIS[label] ?? null;

// Convertit ancien champ texte "etat" -> clé approchée
const legacyToKeyDevis = (raw = "") => {
    const s = String(raw).toLowerCase();
    if (s.includes("accept")) return "accepte";
    if (s.includes("abandon")) return "abandonne";
    if (s.includes("envoy")) return "envoye";
    return "brouillon";
};

// Accepte soit une clé, soit un libellé, soit un ancien texte
const toStatusKeyDevis = (v) => {
    if (!v) return "brouillon";
    const s = String(v).toLowerCase();
    if (s === "brouillon" || s === "envoye" || s === "accepte" || s === "abandonne") return s;
    return labelToStatusDevis(v) || legacyToKeyDevis(v);
};

// Normalise un devis chargé depuis l’API pour garantir la présence de "status"
const normalizeDevisRow = (d) => {
    const key = d.status || legacyToKeyDevis(d.etat);
    const safeKey = toStatusKeyDevis(key);
    const safeLabel = LABELS_DEVIS[safeKey] || "Brouillon";
    return { ...d, status: safeKey, etat: d.etat || safeLabel };
};

/* =========================
   Page
   ========================= */
export default function DocumentsCentreDevis() {
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [exportOpen, setExportOpen] = useState(false);

    // Filtres & tri (locaux à la page devis)
    const [filters, setFilters] = useState({
        etat: "", // libellé (ex: "Envoyé")
        client: "",
        from: "",
        to: "",
        minHT: "",
        maxHT: "",
    });
    const [sort, setSort] = useState({ key: "date", dir: "desc" });

    // Sélection (actions multi)
    const [selected, setSelected] = useState(new Set());

    // Modale coûts
    const [costOpen, setCostOpen] = useState(false);
    const [costDoc, setCostDoc] = useState(null);

    const navigate = useNavigate();

    /* ===== Load devis ===== */
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const res = await axiosInstance.get("/devis");
                const sorted = (res.data || [])
                    .map(normalizeDevisRow)
                    .sort(
                        (a, b) =>
                            new Date(b.date_devis || b.created_at) -
                            new Date(a.date_devis || a.created_at) ||
                            (b.id ?? 0) - (a.id ?? 0)
                    );
                if (mounted) setRows(sorted);
            } catch (e) {
                console.error("Chargement devis échoué:", e);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    /* ===== Utils filtre ===== */
    const textMatch = (hay, needle) =>
        String(hay || "").toLowerCase().includes(String(needle).toLowerCase());

    const inDateRange = (iso) => {
        if (!filters.from && !filters.to) return true;
        const t = iso ? new Date(iso).getTime() : NaN;
        if (Number.isNaN(t)) return false;
        if (filters.from && t < new Date(filters.from).getTime()) return false;
        if (filters.to && t > new Date(filters.to).getTime() + 24 * 3600 * 1000 - 1) return false;
        return true;
    };

    const inAmountRange = (ht) => {
        const min = Number(filters.minHT || NaN);
        const max = Number(filters.maxHT || NaN);
        if (!Number.isFinite(min) && !Number.isFinite(max)) return true;
        if (Number.isFinite(min) && ht < min) return false;
        if (Number.isFinite(max) && ht > max) return false;
        return true;
    };

    /* ===== Datalist clients ===== */
    const clientsSet = useMemo(() => {
        const s = new Set();
        for (const d of rows) if (d?.client_nom) s.add(d.client_nom);
        return Array.from(s)
            .filter(Boolean)
            .sort((a, b) => String(a).localeCompare(String(b), "fr", { sensitivity: "base" }));
    }, [rows]);

    /* ===== Filtrage ===== */
    const filtered = useMemo(() => {
        return rows.filter((d) => {
            const okQ =
                !q ||
                [d.numero_devis, d.client_nom, d.client_adresse, d.projet_nom].some((v) => textMatch(v, q));
            if (!okQ) return false;

            if (filters.client && !textMatch(d.client_nom, filters.client)) return false;

            // filtre *sur la clé* (status), en lisant d.status ou en retombant sur legacy
            if (filters.etat) {
                const wantedKey = labelToStatusDevis(filters.etat); // clé depuis libellé filtre
                const docKey = d.status || legacyToKeyDevis(d.etat);
                if (wantedKey && docKey !== wantedKey) return false;
            }

            const { totalHT } = totalsWithCost(d, num(d.total_ht ?? d.montant_ht));
            if (!inAmountRange(totalHT)) return false;

            const refDate = d.date_devis || d.created_at;
            if (!inDateRange(refDate)) return false;

            return true;
        });
    }, [rows, q, filters]);

    /* ===== Tri ===== */
    const sortFn = (a, b) => {
        const dir = sort.dir === "asc" ? 1 : -1;
        const get = (x) => {
            const { totalHT, totalCost, marge, taux } = totalsWithCost(x, num(x.total_ht ?? x.montant_ht));
            const date = x.date_devis || x.created_at;

            // PU moyen
            const lignes =
                Array.isArray(x.contenu)
                    ? x.contenu
                    : x.contenu
                        ? JSON.parse(x.contenu)
                        : Array.isArray(x.lignes)
                            ? x.lignes
                            : x.lignes
                                ? JSON.parse(x.lignes)
                                : [];
            const q = lignes.reduce((s, l) => s + (Number(l.quantite ?? l.qty ?? 1) || 1), 0);
            const pu = q > 0 ? totalHT / q : totalHT;

            switch (sort.key) {
                case "date":
                    return new Date(date).getTime();
                case "numero":
                    return String(x.numero_devis || "");
                case "client":
                    return String(x.client_nom || "");
                case "ht":
                    return totalHT;
                case "cost":
                    return totalCost;
                case "marge":
                    return marge;
                case "taux":
                    return taux;
                case "ttc":
                    return Number(x.total_ttc ?? x.montant_ttc ?? x.total ?? 0);
                case "pu":
                    return pu;
                default:
                    return new Date(date).getTime();
            }
        };
        const A = get(a), B = get(b);
        if (typeof A === "string" || typeof B === "string") {
            return dir * String(A).localeCompare(String(B), "fr", { sensitivity: "base" });
        }
        return dir * (A - B);
    };

    const filteredSorted = useMemo(() => filtered.slice().sort(sortFn), [filtered, sort]);

    /* ===== Groupage par mois ===== */
    const groups = useMemo(() => {
        const map = new Map();
        for (const d of filteredSorted) {
            const key = toMonthKey(d.date_devis || d.created_at);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(d);
        }
        return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
    }, [filteredSorted]);

    /* ===== Sélection helpers ===== */
    const toggleOne = (id) => {
        setSelected((s) => {
            const n = new Set(s);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    };

    /* ===== Navigation ligne ===== */
    const onRowClick = (doc) => navigate(`/afficher-devis?id=${doc.id}`);

    /* ===== Changement d’état inline ===== */
    const onEtatChange = async (id, input) => {
        // "input" peut être un *libellé* ou directement une *clé*
        const newKey = toStatusKeyDevis(input) || "brouillon";
        const newLabel = LABELS_DEVIS[newKey] || "Brouillon";

        // MAJ optimiste : on écrit *status* (clé) et *etat* (libellé) pour compat totale
        setRows((prev) => prev.map((d) => (d.id === id ? { ...d, status: newKey, etat: newLabel } : d)));

        try {
            await persistEtatDevis(id, newLabel); // ta version SharedCentre doit accepter le libellé ; elle enverra {status/etat}
            localStorage.setItem("docs:refresh", String(Date.now()));
        } catch (e) {
            console.warn("Maj état devis KO:", e);
        }
    };

    /* ===== Modale Coûts ===== */
    const openCost = (doc) => {
        setCostDoc(doc);
        setCostOpen(true);
    };
    const onCostSaved = (newCost) => {
        setRows((prev) =>
            prev.map((d) =>
                d.id === costDoc.id ? { ...d, meta: { ...(d.meta || {}), cout_total: newCost } } : d
            )
        );
    };

    /* ===== KPIs ===== */
    const kpis = useMemo(() => {
        const acc = filteredSorted.reduce(
            (a, x) => {
                const { totalHT, totalCost, marge } = totalsWithCost(x, num(x.total_ht ?? x.montant_ht));
                a.ht += totalHT;
                a.cost += totalCost;
                a.marge += Math.max(0, marge);
                return a;
            },
            { ht: 0, cost: 0, marge: 0 }
        );
        return { ht: acc.ht, marge: acc.marge, taux: acc.ht > 0 ? acc.marge / acc.ht : 0 };
    }, [filteredSorted]);

    if (loading) return <p className="text-center py-10">⏳ Chargement…</p>;

    return (
        <div className="p-1 space-y-4">
            <TitleRow title="Devis" kpis={kpis} />

            <ToolbarRow
                q={q}
                setQ={setQ}
                tab="devis"
                filters={filters}
                setFilters={setFilters}
                uniqueClients={clientsSet}
                sort={sort}
                setSort={setSort}
                onExport={() => setExportOpen(true)}
                onCreateDevis={() => navigate("/creer-devis")}
                onCreateFacture={() => navigate("/creer-facture")}

            />

            {groups.length === 0 && (
                <p className="text-center py-8 text-gray-500">Aucun document.</p>
            )}

            {groups.map(([monthKey, monthRows]) => (
                <section key={monthKey} className="space-y-2">
                    <div className="sticky top-0 z-20 bg-slate-100/80 backdrop-blur px-2 py-1 rounded border text-sm font-semibold capitalize">
                        {toMonthLabel(monthKey)}
                    </div>

                    <TableDocs
                        kind="devis"
                        rows={monthRows}
                        onRowClick={onRowClick}
                        onEtatChange={onEtatChange}
                        onOpenCost={openCost}
                        selection={{
                            ids: selected,
                            allChecked: monthRows.length > 0 && monthRows.every((r) => selected.has(r.id)),
                        }}
                        toggleOne={toggleOne}
                        toggleAll={() => {
                            const ids = monthRows.map((r) => r.id);
                            const all = ids.every((id) => selected.has(id));
                            setSelected((prev) => {
                                const n = new Set(prev);
                                if (all) ids.forEach((id) => n.delete(id));
                                else ids.forEach((id) => n.add(id));
                                return n;
                            });
                        }}
                        sortable={(key) =>
                            setSort((s) => ({
                                key,
                                dir: s.key === key ? (s.dir === "asc" ? "desc" : "asc") : "asc",
                            }))
                        }
                    />
                </section>
            ))}

            <ExportModal
                open={exportOpen}
                onClose={() => setExportOpen(false)}
                tab="devis"
                devis={filteredSorted}
                factures={[]}
            />

            <CostMarginModal
                open={costOpen}
                onClose={() => setCostOpen(false)}
                kind="devis"
                doc={costDoc || {}}
                onSaved={onCostSaved}
            />
        </div>
    );
}
